import React from 'react';
import { motion } from 'framer-motion';
import { Radio } from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-black/5 dark:border-white/10 last:border-0">
      <span className="text-sm opacity-60">{k}</span>
      <span className="font-bold tabular-nums">{v}</span>
    </div>
  );
}

export default function LivePage() {
  const { sensorData, history, connected, status } = useMQTT();

  return (
    <div className="grid gap-4 sm:gap-5">
      <motion.div
        layout
        className="card-surface p-5 flex items-center gap-4"
      >
        <span className="relative inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white">
          <span className="absolute inset-0 rounded-2xl bg-emerald-500 animate-pulse-soft opacity-50" />
          <Radio className="relative w-6 h-6" />
        </span>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold tracking-tight">Live Monitoring</h2>
          <p className="text-sm opacity-70">
            {connected ? 'Connected · receiving updates' : `MQTT: ${status}`}
          </p>
        </div>
        <span className="chip bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-soft" />
          {history.length} samples
        </span>
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card-surface p-5">
          <p className="text-xs font-bold uppercase opacity-60 tracking-wider mb-2">Latest Reading</p>
          <Row k="Temperature" v={`${sensorData.temperature.toFixed(1)} °C`} />
          <Row k="Humidity"    v={`${sensorData.humidity.toFixed(1)} %`} />
          <Row k="Vibration"   v={`${sensorData.vibration.toFixed(2)} m/s²`} />
          <Row k="Battery"     v={`${sensorData.battery.toFixed(0)} %`} />
          <Row k="Motion"      v={sensorData.motion ? 'Detected' : 'Still'} />
          <Row k="Latitude"    v={sensorData.gps.latitude  ? sensorData.gps.latitude.toFixed(5)  : '—'} />
          <Row k="Longitude"   v={sensorData.gps.longitude ? sensorData.gps.longitude.toFixed(5) : '—'} />
          <Row k="Accuracy"    v={sensorData.gps.accuracy  ? `±${sensorData.gps.accuracy.toFixed(0)} m` : '—'} />
          <Row k="Timestamp"   v={sensorData.timestamp.toLocaleTimeString()} />
        </div>

        <div className="card-surface p-5">
          <p className="text-xs font-bold uppercase opacity-60 tracking-wider mb-3">Recent Stream</p>
          <div className="space-y-1.5 max-h-[360px] overflow-auto pr-1 font-mono text-[11px] leading-relaxed">
            {history.slice(-25).reverse().map((h, i) => (
              <div key={i} className="opacity-80">
                <span className="opacity-50">{new Date(h.timestamp).toLocaleTimeString()}</span>
                {' · '}T={h.temperature.toFixed(1)}°
                {' '}H={h.humidity.toFixed(0)}%
                {' '}V={Number(h.vibration ?? 0).toFixed(2)}
              </div>
            ))}
            {history.length === 0 && (
              <div className="opacity-50">Waiting for first sample…</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
